import React from 'react';
import { Helmet } from 'react-helmet';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  url, 
  image,
  type = "website",
  locale = "fr_FR"
}) => {
  const baseUrl = "https://ultraze.fr";
  const fullUrl = url ? `${baseUrl}${url}` : baseUrl;
  const fullImage = image ? `${baseUrl}${image}` : `${baseUrl}/logo.png`;
  
  // Keywords étendus pour couvrir tous les termes importants
  const baseKeywords = [
    // Marques principales
    "ultraze", "ultraze fa", "ultraze wl", "ultrazewl", "ultraze whitelist", "ultraze.fr",
    
    // Shino et contenu
    "shino", "shinozatv", "shinoza tiktok", "shinoza", "shino twitch", "shino youtube",
    
    // FiveM et GTA
    "fivem", "gta rp", "serveur fivem", "roleplay gta", "gta 5 rp", "five m", "gta v rp", 
    "gta five m", "gta5 rp", "gta v roleplay", "gta online rp", "gta multiplayer",
    
    // Langue et localisation
    "rp français", "french rp", "serveur roleplay", "serveur rp français", "rp serious", 
    "rp réaliste", "rp francophone", "gaming français", "communauté francophone",
    
    // Projets et communautés
    "side life", "sidelife fivem", "side life fivem", "blf fivem", "blf side life",
    "communauté fivem", "histoire fivem", "histoire ultraze", "histoire side life",
    
    // Serveurs et développement
    "serveur gta v", "serveur gta 5", "fivem server", "roleplay server", "serveur rp",
    "serveur fivem français", "serveur gta rp", "serveur roleplay serious",
    
    // Thématiques gaming
    "multiplayer rp", "gaming community", "virtual world", "role playing game", 
    "grand theft auto v", "gta v", "gta 5", "gta online",
    
    // Spécialisations RP
    "fivem france", "fivem communautaire", "ville virtuelle", "économie virtuelle", 
    "police rp", "ambulance rp", "criminalité rp", "vie urbaine", "simulation urbaine",
    
    // Communauté et social
    "communauté rp", "projet fivem", "développement serveur", "création serveur fivem",
    "gaming youtube", "streamer fivem", "créateur contenu gaming", "youtube gaming fr",
    
    // Termes complémentaires
    "jeux vidéo", "gaming", "jeu en ligne", "communauté gaming", "joueur fivem",
    "expérience fivem", "histoire gaming", "communauté sérieuse", "serveur qualité"
  ];
  
  const allKeywords = keywords ? [...baseKeywords, ...keywords.split(',')] : baseKeywords;
  const keywordsString = allKeywords.join(', ');

  return (
    <Helmet>
      {/* Titres */}
      <title>{title}</title>
      <meta name="title" content={title} />
      
      {/* Description */}
      <meta name="description" content={description} />
      
      {/* Keywords */}
      <meta name="keywords" content={keywordsString} />
      
      {/* Auteur et langue */}
      <meta name="author" content="BLF | SideLife" />
      <meta name="language" content="French" />
      <meta name="geo.region" content="FR" />
      <meta name="geo.placename" content="France" />
      
      {/* Robots et indexation */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Ultraze FA" />
      <meta property="og:locale" content={locale} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullImage} />
      <meta property="twitter:image:alt" content={title} />
      
      {/* LinkedIn */}
      <meta property="og:image:type" content="image/png" />
      
      {/* Pinterest */}
      <meta name="pinterest-rich-pin" content="true" />
      
      {/* Article specific metadata */}
      <meta property="article:tag" content="FiveM" />
      <meta property="article:tag" content="GTA RP" />
      <meta property="article:tag" content="Serveur RP" />
      <meta property="article:tag" content="Communauté Gaming" />
      <meta property="article:tag" content="Shino" />
      <meta property="article:tag" content="Ultraze" />
      <meta property="article:tag" content="SideLife" />
      <meta property="article:tag" content="RP Français" />
      <meta property="article:tag" content="Gaming Francophone" />
      <meta property="article:section" content="Gaming" />
      <meta property="article:published_time" content="2024-01-01T00:00:00+00:00" />
      <meta property="article:modified_time" content="2024-01-16T00:00:00+00:00" />
      <meta property="article:author" content="BLF | SideLife" />
      
      {/* Mobile et responsive */}
      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      
      {/* Application metadata */}
      <meta name="application-name" content="Ultraze FA" />
      <meta name="theme-color" content="#8a2be2" />
      <meta name="msapplication-TileColor" content="#8a2be2" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      
      {/* Distribution et rating */}
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="coverage" content="Worldwide" />
      <meta name="target" content="all" />
      
      {/* Favicon et icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
      
      {/* Preconnect pour performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* DNS prefetch */}
      <link rel="dns-prefetch" href="//ultraze.fr" />
      
      {/* Prefetch critical resources */}
      <link rel="prefetch" href="/logo.png" as="image" />
      
      {/* Additional SEO Enhancements */}
      <meta name="referrer" content="no-referrer-when-downgrade" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="geo.position" content="48.8566;2.3522" />
      <meta name="ICBM" content="48.8566, 2.3522" />
      
      {/* Structured Data JSON-LD - Enhanced */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebSite",
              "@id": `${baseUrl}/#website`,
              "url": baseUrl,
              "name": "Ultraze FA",
              "description": "L'histoire vraie d'un serveur FiveM RP et la naissance de SideLife",
              "publisher": {
                "@id": `${baseUrl}/#organization`
              },
              "inLanguage": "fr-FR"
            },
            {
              "@type": "Organization",
              "@id": `${baseUrl}/#organization`,
              "name": "SideLife",
              "url": baseUrl,
              "logo": {
                "@type": "ImageObject",
                "url": fullImage,
                "width": 512,
                "height": 512
              },
              "sameAs": [
                "https://discord.gg/ZQh79htaUf"
              ]
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Accueil",
                  "item": baseUrl
                }
              ]
            }
          ]
        })}
      </script>
    </Helmet>
  );
};

export default SEO;